import { CommonModule, Location } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, firstValueFrom, forkJoin, switchMap, tap } from 'rxjs';

import { AutocompleteComponent } from '@mm-aggregator/drf-dynamics';
import * as XLSX from 'xlsx';
import sodium from 'libsodium-wrappers';

import { UI, type UiOption } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import {
  MerchantHubService,
  type ConfigurationResponse,
  type OperationsResponse,
  type SettlementLimits,
} from '../../services/merchant-hub.service';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { TransactionHubService } from '../../services/transaction-hub.service';
import { goBackOrNavigate } from '../../utils/navigation';

export interface ParsedLine {
  destination_account: string;
  destination_name?: string;
  amount: string;
  merchant_reference?: string;
  purpose?: string;
}

interface Issue {
  row: number | null;
  field: string;
  code: string;
  message: string;
}

interface ProviderChoice {
  code: string;
  name: string;
  country: string;
}

/** Problems with one line, found before sending (the server checks everything again). */
export function lineProblems(lines: ParsedLine[]): string[][] {
  const seen = new Set<string>();
  return lines.map((line) => {
    const problems: string[] = [];
    const amount = Number(String(line.amount).replace(',', '.'));
    if (!line.destination_account.trim()) {
      problems.push('Account missing');
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      problems.push('Invalid amount');
    } else if (Math.round(amount * 100) / 100 !== amount) {
      problems.push('Max 2 decimals');
    }
    const ref = (line.merchant_reference ?? '').trim();
    if (ref) {
      if (seen.has(ref)) {
        problems.push('Duplicate reference');
      }
      seen.add(ref);
    }
    return problems;
  });
}

/**
 * Create a batch for one merchant: pick its application, wallet and provider (no raw ids), load a spreadsheet or CSV,
 * check the lines and the declared total and count, then seal the payload with the server key and send it.
 * Any problem rejects the whole batch with a per-row report; nothing is sent.
 */
@Component({
  selector: 'app-batch-disbursement-create',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, AutocompleteComponent, ...UI],
  templateUrl: './batch-disbursement-create.component.html',
})
export class BatchDisbursementCreateComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;

  private readonly hub = inject(TransactionHubService);
  private readonly merchants = inject(MerchantHubService);
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly shellRoutes = inject(ShellRoutesService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.providerSearch$
      .pipe(
        tap(() => this.providersLoading.set(true)),
        switchMap((term) =>
          this.merchants.configurationProviders(this.merchantId, {
            page: 1,
            search: term,
            country_code: this.wallet()?.country_code ?? '',
            status: 'enabled',
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          const keep = this.providers().filter((p) => p.code === this.providerCode);
          const found = res.results
            .filter((p) => p.code !== this.providerCode)
            .map((p) => ({ code: p.code, name: p.name, country: p.country.code }));
          this.providers.set([...keep, ...found]);
          this.providersLoading.set(false);
        },
        error: () => this.providersLoading.set(false),
      });
  }

  merchantId = '';
  merchantName = '';
  batchName = '';
  applicationId = '';
  walletId = '';
  providerCode = '';
  webhookUrl = '';
  fileName = '';
  declaredTotal = '';
  declaredCount = '';

  readonly applications = signal<ConfigurationResponse['operation_types']['applications']>([]);
  readonly wallets = signal<OperationsResponse['wallets']>([]);
  /** Providers of the chosen wallet's country: fetched from the API (searchable, 20 at a time), never the whole list. */
  readonly providers = signal<ProviderChoice[]>([]);
  readonly providersLoading = signal(false);
  readonly providerSearch$ = new Subject<string>();
  readonly limits = signal<SettlementLimits | null>(null);
  readonly walletSelected = signal('');
  readonly lines = signal<ParsedLine[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<{ id: string; reference: string } | null>(null);
  readonly issues = signal<Issue[]>([]);

  readonly problems = computed(() => lineProblems(this.lines()));
  readonly problemCount = computed(() => this.problems().filter((p) => p.length > 0).length);
  readonly total = computed(() =>
    this.lines().reduce((sum, l) => {
      const n = Number(String(l.amount).replace(',', '.'));
      return Number.isFinite(n) && n > 0 ? sum + n : sum;
    }, 0),
  );
  readonly wallet = computed(() => this.wallets().find((w) => w.id === this.walletSelected()) ?? null);
  readonly providerOptions = computed<UiOption<string>[]>(() =>
    this.providers().map((p) => ({ value: p.code, label: `${p.name} (${p.code})` })),
  );
  readonly withinLimit = computed(
    () => this.lines().length <= (this.limits()?.bulk_effective_max_lines ?? Infinity),
  );

  ngOnInit(): void {
    const merchant = this.route.snapshot.queryParamMap.get('merchant');
    if (merchant) {
      this.selectMerchant({ id: merchant });
    }
  }

  goBack(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', this.entityConfig.key));
  }

  selectMerchant(item: Record<string, unknown>): void {
    this.merchantId = String(item['id'] ?? '');
    this.merchantName = String(item['label'] ?? item['name'] ?? '');
    this.applicationId = this.walletId = this.providerCode = '';
    this.walletSelected.set('');
    if (!this.merchantId) {
      return;
    }
    forkJoin({
      config: this.merchants.configuration(this.merchantId),
      operations: this.merchants.operations(this.merchantId),
      settlements: this.merchants.settlements(this.merchantId),
    }).subscribe({
      next: ({ config, operations, settlements }) => {
        const active = config.operation_types.applications.filter((a) => a.is_active);
        this.applications.set(active);
        if (active.length === 1) {
          this.applicationId = active[0].id;
        }
        this.wallets.set(operations.wallets.filter((w) => w.is_active));
        this.limits.set(settlements.limits);
      },
      error: () => this.error.set('Could not load this merchant (applications, wallets and providers).'),
    });
  }

  clearMerchant(): void {
    this.selectMerchant({});
    this.applications.set([]);
    this.wallets.set([]);
    this.providers.set([]);
  }

  chooseWallet(id: string): void {
    this.walletId = id;
    this.walletSelected.set(id);
    this.providerCode = '';
    this.providers.set([]);
    this.providerSearch$.next('');
  }

  onFileSelected(ev: Event): void {
    const file = (ev.target as HTMLInputElement).files?.[0];
    if (!file) {
      return;
    }
    this.fileName = file.name;
    this.error.set(null);
    this.issues.set([]);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array', raw: true });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
        const parsed: ParsedLine[] = rows
          .map((row) => ({
            destination_account: String(
              this.cell(row, ['destination_account', 'Destination account', 'account', 'phone']) || '',
            ).trim(),
            destination_name: String(
              this.cell(row, ['destination_name', 'Destination name', 'name']) || '',
            ).trim(),
            amount: String(this.cell(row, ['amount', 'Amount']) || '').trim(),
            merchant_reference: String(
              this.cell(row, ['merchant_reference', 'Merchant reference', 'reference']) || '',
            ).trim(),
            purpose: String(this.cell(row, ['purpose', 'Purpose']) || 'Batch disbursement'),
          }))
          .filter((l) => l.destination_account || l.amount);
        if (!parsed.length) {
          this.error.set('No lines found (columns destination_account and amount are required).');
        }
        this.lines.set(parsed);
      } catch (err) {
        this.error.set(err instanceof Error ? err.message : 'Failed to read the file.');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  private cell(row: Record<string, unknown>, keys: string[]): unknown {
    const lower = Object.fromEntries(
      Object.entries(row).map(([k, v]) => [k.toLowerCase().replace(/\s+/g, '_'), v]),
    );
    for (const k of keys) {
      const v = lower[k.toLowerCase().replace(/\s+/g, '_')];
      if (v !== '' && v !== null && v !== undefined) {
        return v;
      }
    }
    return '';
  }

  totalMatches(): boolean {
    const typed = Number(this.declaredTotal.replace(',', '.'));
    return this.declaredTotal !== '' && Number.isFinite(typed) && Math.abs(typed - this.total()) < 0.005;
  }

  countMatches(): boolean {
    return this.declaredCount !== '' && Number(this.declaredCount) === this.lines().length;
  }

  canSubmit(): boolean {
    return (
      !!this.merchantId &&
      !!this.batchName.trim() &&
      !!this.applicationId &&
      !!this.walletSelected() &&
      !!this.providerCode &&
      this.lines().length > 0 &&
      this.problemCount() === 0 &&
      this.withinLimit() &&
      this.totalMatches() &&
      this.countMatches()
    );
  }

  async submit(): Promise<void> {
    this.error.set(null);
    this.issues.set([]);
    this.success.set(null);
    const wallet = this.wallet();
    if (!this.canSubmit() || !wallet) {
      this.error.set('Complete the form and the double-check before sending.');
      return;
    }
    this.loading.set(true);
    try {
      await sodium.ready;
      const keyResp = await firstValueFrom(this.hub.getBatchUploadKey());
      const plain = new TextEncoder().encode(JSON.stringify({ lines: this.lines() }));
      const ciphertext = sodium.to_base64(
        sodium.crypto_box_seal(plain, sodium.from_base64(keyResp.public_key)),
      );
      const res = await firstValueFrom(
        this.hub.submitSealedBatch({
          ciphertext,
          batch_name: this.batchName.trim(),
          application: this.applicationId,
          wallet: wallet.id,
          currency: wallet.currency_code,
          provider_code: this.providerCode,
          webhook_url: this.webhookUrl.trim(),
          file_name: this.fileName,
          declared_total_amount: this.total().toFixed(2),
          declared_line_count: this.lines().length,
          idempotency_key: crypto.randomUUID(),
        }),
      );
      const created = res as { id?: string; batch_reference?: string };
      this.success.set({ id: created.id ?? '', reference: created.batch_reference ?? '' });
    } catch (e) {
      const body = (e as { error?: { code?: string; errors?: Issue[]; detail?: string } })?.error;
      if (body?.code === 'BATCH_REJECTED' && Array.isArray(body.errors)) {
        this.issues.set(body.errors);
        this.error.set('Nothing was sent. Correct the file and try again.');
      } else {
        this.error.set(body?.detail ?? (e instanceof Error ? e.message : 'Submit failed.'));
      }
    } finally {
      this.loading.set(false);
    }
  }

  batchLink(): (string | number | boolean)[] {
    return this.shellRoutes.commands('manage', 'batch-disbursements', this.success()?.id ?? '');
  }
}
